import React, { useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation } from "@apollo/client";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Tooltip } from "@mui/material";
import DomainChip from "../../../components/DomainChip/DomainChip";
import UserSelectionDialog from "../../../components/UserSelectionDialog";
import ControlOwnerAvatar from "../../../components/ControlOwnerAvatar/ControlOwnerAvatar";
import { UPDATE_TASK_OWNER_BULK } from "../../../apis/task-apis/taskApis";
import { TaskStatus } from "../../../utils/enums";
import { useNavigate } from "react-router-dom";

const TaskDetails = ({ task, onTaskUpdated, onTaskOwnerChanged }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [descriptionExpanded, setDescriptionExpanded] = useState(true);
  const [visitSourceHover, setVisitSourceHover] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [updateTaskOwner, { loading: ownerSaving }] = useMutation(
    UPDATE_TASK_OWNER_BULK
  );

  if (!task) {
    return (
      <Box
        sx={{
          flex: 1,
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Typography
          sx={{
            fontSize: "15px",
            color: theme.customColors.text.secondary,
            textAlign: "center",
          }}
        >
          Select a task to view details
        </Typography>
      </Box>
    );
  }

  const getRemediationSteps = (steps) => {
    if (!steps) return [];
    if (typeof steps === "object" && steps.steps) return steps.steps;
    try {
      const parsed = JSON.parse(steps);
      return parsed?.steps || (Array.isArray(parsed) ? parsed : []);
    } catch {
      return [];
    }
  };

  const remediationSteps = getRemediationSteps(task.remediation_steps);
  const templateBaseUrl = process.env.REACT_APP_POLICY_DOC_URL;
  const currentOwner = task.task_owner_data;

  const handleOwnerConfirm = async (user) => {
    await updateTaskOwner({
      variables: {
        task_ids: [task.id],
        task_owner_id: user?.id ?? null,
      },
    });
    onTaskUpdated?.({
      ...task,
      task_owner_id: user?.id ?? null,
      task_owner_data: user
        ? {
          id: user.id,
          name: user.name,
          email: user.email,
          first_name: "",
          last_name: "",
        }
        : null,
    });
    onTaskOwnerChanged?.();
    setOwnerDialogOpen(false);
  };

  const handleGoToSource = () => {
    if (task.action_uri) {
      let path = task.action_uri.startsWith("/")
        ? task.action_uri.slice(1)
        : task.action_uri;
      let fullUrl = `${window.location.origin}/${path}`;
      window.open(fullUrl, "_blank", "noopener,noreferrer");
    }
  };

  const renderTextWithLinks = (text) => {
    if (!text) return text;

    const combinedRegex =
      /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = combinedRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      if (match[1]) {
        const linkText = match[2];
        const linkUrl = match[3]
          .replace(/<action_uri>/g, task.action_uri || "")
          .trim();
        parts.push(
          <Typography
            key={`link-${keyIndex++}`}
            component="span"
            onClick={() => {
              if (linkUrl.startsWith("/templates/")) {
                const downloadUrl = `${templateBaseUrl}${linkUrl}`;
                const linkEl = document.createElement("a");
                linkEl.href = downloadUrl;
                linkEl.download = linkUrl.split("/").pop();
                linkEl.click();
              } else {
                window.open(linkUrl, "_blank", "noopener,noreferrer");
              }
            }}
            sx={{
              fontSize: "inherit",
              color: alpha(theme.palette.primary.main, 0.8),
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
                color: theme.palette.primary.dark,
              },
            }}
          >
            {linkText}
          </Typography>
        );
      } else if (match[4]) {
        const boldText = match[5];
        parts.push(
          <Typography
            key={`bold-${keyIndex++}`}
            component="span"
            sx={{
              fontSize: "inherit",
              fontWeight: 600,
            }}
          >
            {boldText}
          </Typography>
        );
      } else if (match[6]) {
        const boldText = match[7];
        parts.push(
          <Typography
            key={`bold-${keyIndex++}`}
            component="span"
            sx={{
              fontSize: "inherit",
              fontWeight: 600,
            }}
          >
            {boldText}
          </Typography>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: theme.customColors.text.strong,
              lineHeight: 1.4,
              letterSpacing: "normal",
              flex: 1,
            }}
          >
            {task.title}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Chip
            label={task.status_title || task.status_key}
            sx={{
              backgroundColor:
                task.status_key === TaskStatus.RESOLVED
                  ? theme.customColors.status.success.bg
                  : theme.customColors.status.error.bg,
              color:
                task.status_key === TaskStatus.RESOLVED
                  ? theme.customColors.status.success.text
                  : theme.customColors.status.error.text,
              fontWeight: 700,
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              height: "28px",
              borderRadius: "6px",
            }}
          />

          {task.domains && task.domains.length > 0 && (
            <>
              {task.domains.map((domain, idx) => (
                <DomainChip
                  key={idx}
                  domainKey={domain.key}
                  domainTitle={domain.title}
                />
              ))}
            </>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mt: 1.75,
            px: 1.25,
            py: 0.75,
            borderRadius: "8px",
            width: "fit-content",
            border: `1px solid ${alpha(theme.customColors.border.main, 0.25)}`,
            backgroundColor: alpha(theme.customColors.border.main, 0.04),
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary, fontWeight: 500, mr: 0.25 }}
          >
            Owner:
          </Typography>
          <ControlOwnerAvatar
            owner={currentOwner}
            size={24}
            fontSize="0.62rem"
            fontWeight={700}
          />
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.strong, fontWeight: 500 }}
          >
            {currentOwner?.name || currentOwner?.email || "Unassigned"}
          </Typography>
          <Tooltip title="Change owner" arrow>
            <IconButton
              size="small"
              onClick={() => setOwnerDialogOpen(true)}
              sx={{
                p: 0.25,
                ml: 0.25,
                color: theme.customColors.icons?.dark || "#6B7280",
                "&:hover": { backgroundColor: "#E8EDF8" },
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2.5,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.customColors.border.main, 0.5),
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: alpha(theme.customColors.border.main, 0.7),
            },
          },
        }}
      >
        {task.description && (
          <Box
            sx={{
              backgroundColor: "#FAFBFC",
              borderRadius: "8px",
              border: `1px solid ${alpha(theme.customColors.border.main, 0.2)}`,
              mb: 2.5,
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.25,
                cursor: "pointer",
                borderBottom: descriptionExpanded
                  ? `1px solid ${alpha(theme.customColors.border.main, 0.15)}`
                  : "none",
                "&:hover": {
                  backgroundColor: alpha(theme.customColors.border.main, 0.05),
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: theme.customColors.text.strong,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Description
              </Typography>
              <IconButton
                size="small"
                sx={{
                  color: theme.customColors.text.secondary,
                  p: 0.5,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                {descriptionExpanded ? (
                  <ExpandLessIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Box>
            <Collapse in={descriptionExpanded}>
              <Box sx={{ px: 2, pt: 0, mt: 0, pb: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: "15px",
                    color: theme.customColors.text.strong,
                    lineHeight: 1.6,
                    fontWeight: 400,
                    letterSpacing: "normal",
                  }}
                >
                  {task.description}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}

        {remediationSteps.length > 0 && (
          <Box
            sx={{
              backgroundColor: "#FAFBFC",
              borderRadius: "8px",
              border: `1px solid ${alpha(theme.customColors.border.main, 0.2)}`,
              p: 2.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: theme.customColors.text.strong,
                mb: 2.5,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              What steps are required to complete this task?
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {remediationSteps.map((step, index) => {
                const stepText =
                  typeof step === "string" ? step : step.description || "";
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: alpha(
                          theme.customColors.border.main,
                          0.2
                        ),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: alpha(theme.customColors.text.secondary, 0.7),
                        }}
                      >
                        {step.order || index + 1}
                      </Typography>
                    </Box>
                    <Typography
                      component="div"
                      sx={{
                        fontSize: "15px",
                        color: theme.customColors.text.strong,
                        lineHeight: 1.6,
                        fontWeight: 400,
                        letterSpacing: "normal",
                        flex: 1,
                      }}
                    >
                      {renderTextWithLinks(stepText)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
      <UserSelectionDialog
        open={ownerDialogOpen}
        onClose={() => setOwnerDialogOpen(false)}
        title="Change Task Owner"
        description="1 task will be updated."
        label="Task owner"
        currentUser={currentOwner ?? null}
        onConfirm={handleOwnerConfirm}
        saving={ownerSaving}
        adminsOnly
        showUnassignOption
      />
    </Box>
  );
};

export default TaskDetails;
